import type { SupportAccount } from '@my/directory-api';
import { useOutletContext } from 'react-router';

export interface AccountContext {
  account: SupportAccount;
  /**
   * Whether `X-Account-Key` is actually on the wire yet. Anything that reads or
   * writes account-scoped data must wait for it, or the request goes out scoped
   * to no account and comes back empty.
   */
  isReady: boolean;

  /**
   * The page header's right-hand action area, for a section to render into.
   *
   * <p>
   * A portal target rather than a prop, because the header is drawn by the
   * layout while the button's state (a form being open, say) belongs to the
   * section. Passing the element back down lets the section own its own button
   * without the layout learning what any of them do.
   * </p>
   *
   * <p>
   * Null on the very first render, before the node exists. Guard on it.
   * </p>
   */
  headerSlot: HTMLElement | null;
}

/**
 * The account the surrounding section is describing, supplied by
 * `AccountSectionLayout`.
 *
 * <p>
 * Its own file so the layout stays a component-only module — a file exporting
 * both a component and a hook breaks fast refresh for everything in it.
 * </p>
 */
export function useAccountContext(): AccountContext {
  return useOutletContext<AccountContext>();
}
